import { authApi } from './api';
import type { User, Customer, MenuItem, Product } from '../types';

// API response format from Auth.Api
interface UserDtoResponse {
  id: string;
  username: string;
  email: string;
  defaultCustomer?: {
    id: string;
    name: string;
  };
  claims?: Array<{
    type: string;
    value: string;
  }>;
  accessibleProducts?: Array<{
    id: string;
    name: string;
    shortName?: string;
    code?: string;
  }>;
  isCustomerAdmin?: boolean;
}

export interface CurrentUserResponse {
  user: User;
  customers: Customer[];
  selectedCustomer?: Customer;
  accessibleProducts: Product[];
  isCustomerAdmin: boolean;
}

export interface MenuResponse {
  items: MenuItem[];
}

export const authService = {
  // Get current authenticated user
  async getCurrentUser(): Promise<CurrentUserResponse> {
    const response = await authApi.get<UserDtoResponse>('/api/user/current');
    const data = response.data;

    const roleClaims = data.claims?.filter(c =>
      c.type === 'role' ||
      c.type.endsWith('/role') ||
      c.type === 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'
    ) || [];

    const user: User = {
      id: data.id,
      email: data.email,
      firstName: data.username.split('@')[0],
      lastName: '',
      roles: roleClaims.map(c => c.value),
    };

    const customers: Customer[] = data.defaultCustomer
      ? [{ id: data.defaultCustomer.id, name: data.defaultCustomer.name }]
      : [];

    // Map accessible products
    const accessibleProducts: Product[] = (data.accessibleProducts || []).map(p => ({
      id: p.id,
      name: p.name,
      code: p.code,
      shortName: p.shortName,
      description: p.shortName,
      url: p.code ? `/products/${p.code.toLowerCase()}` : undefined,
    }));

    return {
      user,
      customers,
      selectedCustomer: data.defaultCustomer
        ? { id: data.defaultCustomer.id, name: data.defaultCustomer.name }
        : undefined,
      accessibleProducts,
      isCustomerAdmin: data.isCustomerAdmin ?? false,
    };
  },

  // Logout user
  async logout(): Promise<void> {
    await authApi.post('/api/user/logout');
  },

  // Get menu items for current user
  async getMenu(): Promise<MenuResponse> {
    const response = await authApi.get<MenuResponse>('/api/menu');
    return response.data;
  },

  // Select a customer (for multi-tenant support)
  async selectCustomer(customerId: string): Promise<void> {
    await authApi.post('/api/user/select-customer', { customerId });
  },

  // Get customers for current user
  async getCustomers(): Promise<Customer[]> {
    const response = await authApi.get<{ customers: Customer[] }>('/api/customer');
    return response.data.customers;
  },
};

export default authService;
